import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { claims } from "../../data/mockData";
import { claimApi } from "../../api/claimApi";
import { eopApi } from "../../api/eopApi";
import { isApiConfigured } from "../../api/axiosClient";
import { LoadingSpinner, ErrorCard } from "../../components/LoadingSpinner";
import { useToast } from "../../components/Toast";

import { useAuth } from "../../auth/AuthContext";
import "bootstrap/dist/css/bootstrap.min.css";

import { CsvFileDownload } from "../../components/CsvFileDownload";
import { PdfFileDownload } from "../../components/PdfFileDownload";

import { process } from "@progress/kendo-data-query";

import { usePermission } from "../../hooks/usePermission";

/* ── API helpers ────────────────────────────────────────────────────
 * The eligibility-style API returns currency as strings ("$165.00") and
 * uses null for missing fields. These helpers normalize on the way to
 * the UI so the components can keep their JSX clean.
 * ───────────────────────────────────────────────────────────────── */

// "$165.00" / "$1,234.56" / 165.00 / null → 165 (number)
const parseAmount = (raw) => {
  if (raw === null || raw === undefined || raw === "") return 0;
  if (typeof raw === "number") return raw;
  const cleaned = String(raw).replace(/[$,\s]/g, "");
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : 0;
};

// "$165.00" → "165.00"  (strip $ for the Line Items table display)
const fmtAmountNoSign = (raw) => parseAmount(raw).toFixed(2);

// 165 → "$165.00"

const fmtCurrency = (n) => `$${(Number(n) || 0).toFixed(2)}`;

//const fmtCurrency = (n) => {
//    console.log(n);
//    `$${(Number(n) || 0).toFixed(2)}`;
//}

//const fmtCurrency = (n) =>
//    new Intl.NumberFormat('en-US', {
//        style: 'currency',
//        currency: 'USD',
//    }).format(
//        Number(String(n).replace(/[$,]/g, '')) || 0
//    );

// null/undefined/'' → "—" (so the UI never renders the literal "null")
const fmtNullDash = (raw) =>
  raw === null || raw === undefined || raw === "" ? "—" : raw;

// Sum the parsed amounts of `field` across an array
const sumAmounts = (rows, field) =>
  (rows || []).reduce((acc, r) => acc + parseAmount(r?.[field]), 0);

/**
 * ExDescriptionInline — renders an Ex Description with a "Show more / Show
 * less" toggle. Truncates to ~120 characters by default; the user clicks
 * the link to expand to the full text and clicks again to collapse.
 * Returns null when the text is empty so the row simply isn't rendered.
 */
const ExDescriptionInline = ({ text }) => {
  const [showMore, setShowMore] = useState(false);

  if (!text) return null;

  const truncate = (s, n) => (s.length > n ? s.slice(0, n).trimEnd() + "…" : s);
  const isLong = text.length > 120;

  return (
    <div
      style={{
        marginTop: 12,
        fontSize: 13,
        color: "#475569",
        lineHeight: 1.55,
      }}
    >
      <strong style={{ color: "#1A1D21" }}>Ex Description:</strong>{" "}
      {showMore ? text : truncate(text, 120)}
      {isLong && (
        <button
          type="button"
          onClick={() => setShowMore(!showMore)}
          style={{
            marginLeft: 6,
            background: "none",
            border: "none",
            padding: 0,
            color: "#0D9488",
            fontWeight: 700,
            cursor: "pointer",
            fontSize: 13,
            fontFamily: "inherit",
          }}
        >
          [{showMore ? "Show less" : "Show more"}]
        </button>
      )}
    </div>
  );
};

const ClaimDetailPage = () => {
  const { claimNo, chqno, billingPro } = useParams();
  const toast = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const { can, canAny, canAll } = usePermission();

  /********************Access permission****************************/
  //const canSearchClaim = can(
  //    'claimAndPayments',
  //    'claimStatus',
  //    'searchClaim'
  //);

  const canRequestClaimEop = can(
    "claimAndPayments",
    "claimStatus",
    "requestClaimEop",
  );

  const canDownloadClaimEop = can(
    "claimAndPayments",
    "claimStatus",
    "downloadClaimEop",
  );

  //const canViewClaimAppeal = can(
  //    'claimAndPayments',
  //    'claimAppeals',
  //    'viewClaimAppeal'
  //);

  const canFileClaimAppeal = can(
    "claimAndPayments",
    "claimAppeals",
    "fileClaimAppeal",
  );

  const canDownloadERA835 = can(
    "claimAndPayments",
    "claimStatus",
    "downloadERA835",
  );

  //alert("canDownloadERA835 : " + canDownloadERA835);
  /********************Access permission****************************/

  const savedState = location.state;
  const [selectedTab, setSelectedTab] = useState(0);
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Pagination for the two detail Grids — line items and EOP history.
  const [lineItemsPage, setLineItemsPage] = useState({ skip: 0, take: 10 });
  const [eopHistoryPage, setEopHistoryPage] = useState({ skip: 0, take: 10 });
  const [eopHistory, setEopHistory] = useState([]);
  const fetchedRef = useRef(false);
  const [accessToken, setAccessToken] = useState("");

  const [claimEopDownload, setClaimEopDownload] = useState(false);
  const [claimEopRequest, setClaimEopRequest] = useState(false);
  const [lineItemsGridData, setLineItemsGridData] = useState([]);
  // const [dataResultLineItems, setDataResultLineItems] = useState(null);

  const [dataStateLineItems, setDataStateLineItems] = useState({
    skip: 0,
    take: 10,
    sort: [],
  });

  // const dataResultLineItems = process(lineItemsGridData, dataStateLineItems);

  //useEffect(() => {
  //    setDataResultLineItems();
  //}, [lineItemsGridData]);
  //  Date formatter
  //const formatDate = (value) => {
  //    if (!value) return 'N/A';
  //    const [mm, dd, yyyy] = value.split('-');
  //    return `${yyyy}/${mm}/${dd}`;
  //};

  const formatDate = (value) => {
    if (!value) return "--";

    // Replace - with /
    const normalized = value.replace(/-/g, "/");
    return normalized;
  };

  //const handleGoBack = () => {
  //    if (window.history.length > 1) {
  //        //localStorage.setItem("cameFromDetails", "true");
  //        alert("Has history");
  //        // navigate(-1);

  //    } else {
  //       // alert("fallback");
  //        navigate("/claims"); // fallback route
  //    }
  //};

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    const fetchClaim = async () => {
      setLoading(true);
      setError(null);

      if (isApiConfigured()) {
        try {
          //alert("Hello1");
          // const response = await claimApi.getClaimDetail(id);
          // setClaim(response.data || null);
          //const payload = {
          //    billingProvider: billingPro,
          //    claimNo: claimNo,
          //    checkNo: chqno,
          //    pageNo: 1,
          //    pageLimit: 1,
          //};
          //const response = await claimApi.getClaimDetail(payload);

          //const response = await claimApi.getClaimDetail(id, user);
          const response = await claimApi.getClaimDetail({
            billingProvider: billingPro,
            claimNo: claimNo,
            checkNo: chqno,
            pageNo: 1,
            pageLimit: 1,
          });

          //setClaim(response.data || null);

          //const cleanedDetails = (response.data.Details || []).map(d => ({
          //    ...d,
          //    Billed_Price: parseFloat((d.Billed_Price || '0').replace('$', '')) || 0,
          //    Plan_Price: parseFloat((d.Plan_Price || '0').replace('$', '')) || 0,
          //    Discount_Amt: parseFloat((d.Discount_Amt || '0').replace('$', '')) || 0,
          //    Paid_Amt: parseFloat((d.Paid_Amt || '0').replace('$', '')) || 0,
          //    Copay_Amt: parseFloat((d.Copay_Amt || '0').replace('$', '')) || 0,
          //    Not_Covered_Amt: parseFloat((d.Not_Covered_Amt || '0').replace('$', '')) || 0,
          //    Deductible_Amt: parseFloat((d.Deductible_Amt || '0').replace('$', '')) || 0,
          //    Co_Ins_Amt: parseFloat((d.Co_Ins_Amt || '0').replace('$', '')) || 0,
          //    COB_Amt: parseFloat((d.COB_Amt || '0').replace('$', '')) || 0,
          //    Your_Rep: parseFloat((d.Your_Rep || '0').replace('$', '')) || 0,
          //}));

          const cleanedDetails = (response.data.Details || []).map((d) => ({
            ...d,
            Billed_Price:
              parseFloat((d.Billed_Price || "0").replace(/[$,]/g, "")) || 0,
            Plan_Price:
              parseFloat((d.Plan_Price || "0").replace(/[$,]/g, "")) || 0,
            Discount_Amt:
              parseFloat((d.Discount_Amt || "0").replace(/[$,]/g, "")) || 0,
            Paid_Amt: parseFloat((d.Paid_Amt || "0").replace(/[$,]/g, "")) || 0,
            Copay_Amt:
              parseFloat((d.Copay_Amt || "0").replace(/[$,]/g, "")) || 0,
            Not_Covered_Amt:
              parseFloat((d.Not_Covered_Amt || "0").replace(/[$,]/g, "")) || 0,
            Deductible_Amt:
              parseFloat((d.Deductible_Amt || "0").replace(/[$,]/g, "")) || 0,
            Co_Ins_Amt:
              parseFloat((d.Co_Ins_Amt || "0").replace(/[$,]/g, "")) || 0,
            COB_Amt: parseFloat((d.COB_Amt || "0").replace(/[$,]/g, "")) || 0,
            Your_Rep: parseFloat((d.Your_Rep || "0").replace(/[$,]/g, "")) || 0,
          }));

          const claimStatus =
            response?.data?.Status_Description[0]?.Status_Description || "";
          const eraRetrivalMethod = response?.data?.ERARetrivalMethod || "";

          setClaim({
            ...response.data,
            Details: cleanedDetails,
            Status: claimStatus,
            ERARetrivalMethod: eraRetrivalMethod,
          });

          console.log("--------------------------------------------");
          console.log("CLAIM STATUS : " + claimStatus);
          console.log("--------------------------------------------");

          const eopHistry = [
            {
              eopNumber: response?.data?.Payee[0]?.Check_Batch,
              checkNumber: response?.data?.Payee[0]?.Check_Number,
              checkDate: formatDate(response?.data?.Payee[0]?.Check_Date),
              //paymentAmount: parseFloat(details[0]?.Paid_Amt?.replace('$', '') || 0),
              paymentAmount: response?.data?.Details?.reduce((sum, d) => {
                const amount =
                  parseFloat((d?.Paid_Amt || "0").replace(/[$,]/g, "")) || 0;
                //return sum + amount;
                return +(sum + Number(amount || 0)).toFixed(2);
              }, 0),
              paymentMethod: "Check",
            },
          ];

          // alert(JSON.stringify(eopHistry));

          const chkNumber = response?.data?.Payee?.[0]?.Check_Number;
          const chkDate = response?.data?.Payee[0]?.Check_Date;

          if (
            chkNumber &&
            chkNumber.trim() !== "" &&
            chkDate &&
            chkDate.trim() !== ""
          ) {
            setEopHistory(eopHistry);
          }
        } catch (err) {
          console.log("ERROR :" + err);
          setError(
            err.response?.data?.message || "Failed to load claim details.",
          );
          setClaim(null);
        } finally {
          setLoading(false);
        }
      } else {
        // Mock data fallback. The route params are claimNo/chqno/billingPro
        // (the old `id` param no longer exists — referencing it threw a
        // ReferenceError here). Mock claims are flat records without the
        // API's Details/Payee arrays this page renders, so resolve to the
        // graceful "Claim Not Found" card rather than crashing.
        const found =
          claims.find(
            (c) =>
              c.claimNumber === claimNo || String(c.id) === String(claimNo),
          ) || null;
        setClaim(found && found.Details ? found : null);
        setLoading(false);
      }
    };

    fetchClaim();
    setAccessToken(localStorage.getItem("sendero_token"));
  }, [claimNo, chqno, billingPro]);

  // True full-width style for the Claim Detail page.
  // Inline overrides for the inner .main-content wrapper:
  const mainContentFullWidthStyle = {
    maxWidth: "none",
    margin: 0,
    padding: "24px 16px", // small inner padding for breathing room
    width: "100%",
    boxSizing: "border-box",
  };

  // Inject a global CSS override that:
  //   (a) REMOVES the OUTER <main>'s horizontal padding while this page is
  //       mounted (so the page goes edge-to-edge of the content area)
  //   (b) FORCES all Kendo TabStrip content + cards inside the Claim Detail
  //       page to span 100% width (Kendo's default applies a flex/auto-width
  //       layout that was making cards take only their natural content width)
  // When the user navigates away, this <style> tag unmounts.
  const fullWidthGlobalCss = `
    body main {
      padding-left: 0 !important;
      padding-right: 0 !important;
    }
    .claim-detail-page,
    .claim-detail-page .k-tabstrip,
    .claim-detail-page .k-tabstrip-content,
    .claim-detail-page .k-content,
    .claim-detail-page .k-animation-container {
      width: 100% !important;
      max-width: none !important;
      box-sizing: border-box !important;
    }
    .claim-detail-page .card {
      width: 100% !important;
      box-sizing: border-box !important;
    }
    /* Suppress the black focus outline that Kendo TabStrip adds to its
       content panel when clicked. The panel isn't a meaningful interactive
       target — keyboard users still get focus rings on the actual tab
       buttons (which we leave alone). */
    .claim-detail-page .k-tabstrip:focus,
    .claim-detail-page .k-tabstrip:focus-visible,
    .claim-detail-page .k-tabstrip-content:focus,
    .claim-detail-page .k-tabstrip-content:focus-visible,
    .claim-detail-page .k-content:focus,
    .claim-detail-page .k-content:focus-visible,
    .claim-detail-page .k-animation-container:focus,
    .claim-detail-page .k-animation-container:focus-visible {
      outline: none !important;
      box-shadow: none !important;
    }
  `;

  // Return to the claim list. Carries the search state the list handed us
  // (results/paging/filters) so it restores without a new search; the list
  // also keeps an in-memory cache, so browser Back / sidebar work too.
  const goBackToClaims = () =>
    navigate("/claims", {
      state: { ...(savedState || {}), fromDetails: true },
    });

  const goBackToAppeal = () => navigate("/appeals");

  const backBar = (
    <div style={{ marginBottom: 12 }}>
      <button
        type="button"
        name="btnBackToClaims"
        onClick={
          savedState.fromClaimAppealList ? goBackToAppeal : goBackToClaims
        }
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "8px 14px",
          borderRadius: 10,
          border: "1px solid #E5E7EB",
          background: "#fff",
          color: "#0F766E",
          fontWeight: 600,
          fontSize: 13,
          cursor: "pointer",
          fontFamily: "inherit",
        }}
      >
        &larr; Back to{" "}
        {savedState.fromClaimAppealList ? "Claim Appeals" : "Claims"}
      </button>
    </div>
  );

  if (loading) {
    return (
      <>
        <style>{fullWidthGlobalCss}</style>
        <div
          className="main-content claim-detail-page"
          style={mainContentFullWidthStyle}
        >
          {backBar}
          <div className="card">
            <LoadingSpinner message="Loading claim details..." />
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <style>{fullWidthGlobalCss}</style>
        <div
          className="main-content claim-detail-page"
          style={mainContentFullWidthStyle}
        >
          {backBar}
          <ErrorCard message={error} onRetry={() => window.location.reload()} />
        </div>
      </>
    );
  }

  if (!claim) {
    return (
      <>
        <style>{fullWidthGlobalCss}</style>
        <div
          className="main-content claim-detail-page"
          style={mainContentFullWidthStyle}
        >
          <div className="card" style={{ textAlign: "center", padding: 48 }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>&#x1F4CB;</div>
            <h2>Claim Not Found</h2>
            <p style={{ color: "#6B7280" }}>
              The claim you're looking for doesn't exist.
            </p>
            <button
              className="btn-primary"
              onClick={goBackToClaims}
              style={{ marginTop: 16 }}
            >
              Back to Claims
            </button>
          </div>
        </div>
      </>
    );
  }

  // Derived fields with fallbacks for data not in mockData
  const paidAmount = claim.paidAmount ?? 0;
  const planPrice = claim.planPrice ?? 0;
  const discountAmount = claim.discountAmount ?? 0;
  const memberResponsibility = claim.memberResponsibility ?? 0;
  const statusDescription = claim.statusDescription || "";
  const exCode = claim.exCode || "";
  const checkNumber = claim.checkNumber || null;
  const checkDate = claim.checkDate || null;
  const eopNumber = claim.eopNumber || null;
  const patientAccountNo = claim.patientAccountNo || "";
  const lineItems = claim.lineItems || [];
  //  const eopHistory = claim.ExCodes || [];

  //const payee = (claim.Payee && claim.Payee[0]) || {
  //    Name: claim.providerName,
  //    Federal_ID: claim.billingProviderId,
  //    Check_Number: claim.checkNumber,
  //    Check_Date: claim.checkDate,
  //};

  // --- Custom cells for Line Items grid ---
  const LineAmountCell = (field) => (props) => {
    const value = props.dataItem[field];
    return (
      <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
        ${(value ?? 0).toFixed(2)}
      </td>
    );
  };

  const ModifierCell = (props) => {
    const value = props.dataItem.modifier;
    return (
      <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
        {value || "-"}
      </td>
    );
  };

  // --- Custom cells for EOP History grid ---
  const EOPAmountCell = (props) => {
    const value = props.dataItem.paymentAmount;
    return (
      <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
        ${(value ?? 0).toFixed(2)}
      </td>
    );
  };

  // --- Line Items totals ---
  const totals = lineItems.reduce(
    (acc, item) => ({
      billed: acc.billed + (item.billedPrice ?? 0),
      paid: acc.paid + (item.paidAmount ?? 0),
      planPrice: acc.planPrice + (item.planPrice ?? 0),
      discount: acc.discount + (item.discountAmount ?? 0),
      memberResp: acc.memberResp + (item.memberResponsibility ?? 0),
    }),
    { billed: 0, paid: 0, planPrice: 0, discount: 0, memberResp: 0 },
  );

  const handleRequestClaimEop = async (
    billProvider,
    checkNo,
    claimNumber,
    paidAmt,
  ) => {
    if (isApiConfigured) {
      //alert("CLICKED");

      try {
        setClaimEopRequest(true);

        if (
          !billProvider?.trim() ||
          !claimNumber?.trim() ||
          !paidAmt?.toString().trim()
        ) {
          toast.error("Something went wrong! try again...", {
            title: "Claim EOP Download",
          });

          return;
        }

        const response = await eopApi.requestClaimEOP({
          billingProvider: billProvider,
          checkNumber: checkNo,
          claimNumber: claimNumber,
          paidAmount: paidAmt.toString(),
          userId: user?.userId,
          token: accessToken,
        });

        if (response.success) {
          const element = document.getElementById(
            `btnReqClaimEop${claimNumber}`,
          );
          if (element) {
            element.style.display = "none";
          }
          toast.success(response.message, { title: "Claim EOP Request" });
        } else {
          toast.error("Oops! Something went wrong.... try again", {
            title: "Claim EOP Request",
          });
        }
      } catch (err) {
        setClaimEopRequest(false);
        toast.error("Oops! Something went wrong.... try again", {
          title: "Claim EOP Request",
        });
      } finally {
        setClaimEopRequest(false);
      }
    }
  };

  const handleDownloadClaimEop = async (
    billProvider,
    claimNumber,
    fileName,
    checkNumber,
  ) => {
    if (isApiConfigured) {
      try {
        setClaimEopDownload(true);

        if (!billProvider?.trim() || !claimNumber?.trim()) {
          toast.error("Something went wrong! try again...", {
            title: "Claim EOP Download",
          });

          return;
        }

        const response = await eopApi.downloadClaimEOP({
          billingProvider: billProvider,
          claimNumber: claimNumber,
          checkNumber: checkNumber,
        });

        //await downloadClaimEopPdfFile(response, fileName);

        await PdfFileDownload(response, fileName);
      } catch (err) {
        setClaimEopDownload(false);
        toast.error("Oops! Something went wrong.... try again", {
          title: "EOP Download",
        });
      } finally {
        setClaimEopDownload(false);
      }
    }
  };
  //const downloadClaimEopPdfFile = (response, fileName) => {

  //    const blob = response.data;

  //    const downloadUrl = window.URL.createObjectURL(blob);

  //    const a = document.createElement("a");
  //    a.href = downloadUrl;
  //    a.download = fileName;
  //    document.body.appendChild(a);
  //    a.click();

  //    a.remove();
  //    window.URL.revokeObjectURL(downloadUrl);
  //};
  return (
    <>
      {/* Inject global CSS to remove outer <main>'s horizontal padding
          ONLY while this page is mounted. Auto-cleans up when user
          navigates away. */}
      <style>{fullWidthGlobalCss}</style>

      <div
        className="main-content claim-detail-page"
        style={mainContentFullWidthStyle}
      >
        {backBar}
        {/* Hero Banner */}
        <div
          style={{
            background: "linear-gradient(135deg, #0F766E, #134E4A)",
            borderRadius: 20,
            padding: "28px 32px",
            marginBottom: 28,
            position: "relative",
            overflow: "hidden",
            boxShadow: "0 8px 30px rgba(13,148,136,0.2)",
          }}
        >
          {/* Decorative circles */}
          <div
            style={{
              position: "absolute",
              top: -40,
              right: -40,
              width: 150,
              height: 150,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.04)",
            }}
          />

          <div
            className="row"
            style={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
            }}
          >
            <div className="col-sm-6 m-2">
              <div style={{ justifyContent: "center" }}>
                <div
                  style={{
                    color: "rgba(255,255,255,0.7)",
                    fontSize: 13,
                    marginBottom: 4,
                  }}
                >
                  Claim Details
                </div>
                <div
                  style={{
                    color: "#fff",
                    fontSize: 26,
                    fontWeight: 800,
                    marginBottom: 8,
                  }}
                >
                  {claim.Details[0].Claim_Number}
                </div>
              </div>
              {/*<div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>*/}
              {/*    <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14 }}>{claim.Details[0].Member_Name}</span>*/}
              {/*    <span style={{*/}
              {/*        padding: '3px 10px',*/}
              {/*        borderRadius: 20,*/}
              {/*        fontSize: 11,*/}
              {/*        fontWeight: 700,*/}
              {/*        background: claim.status === 'Paid' ? 'rgba(16,185,129,0.2)' : claim.status === 'Denied' ? 'rgba(239,68,68,0.2)' : 'rgba(245,158,11,0.2)',*/}
              {/*        color: claim.status === 'Paid' ? '#34D399' : claim.status === 'Denied' ? '#FCA5A5' : '#FCD34D',*/}
              {/*    }}>{claim.status}</span>*/}
              {/*</div>*/}
              <div
                style={{
                  color: "rgba(255,255,255,0.5)",
                  fontSize: 13,
                  marginTop: 4,
                }}
              >
                {claim.providerName}
              </div>
            </div>
            <div className="col-sm-3 m-2 ">
              <div
                style={{ display: "flex", gap: 5, justifyContent: "center" }}
              >
                {claim?.Payee[0]?.Check_Number?.toString().trim() &&
                  !claim?.File_Name?.Pdf_File?.toString().trim() && (
                    <button
                      onClick={() =>
                        handleRequestClaimEop(
                          billingPro,
                          chqno,
                          claimNo,
                          claim?.Details[0]?.Paid_Amt,
                        )
                      }
                      id={`btnReqClaimEop${claimNo}`}
                      style={{
                        padding: "10px 20px",
                        borderRadius: 12,
                        border: "none",
                        background: "#fff",
                        color: "#0F766E",
                        fontWeight: 600,
                        fontSize: 13,
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                    >
                      {claimEopRequest
                        ? "Requesting Claim EOP..."
                        : "Request Claim EOP"}
                    </button>
                  )}

                {claim?.Payee[0]?.Check_Number?.toString().trim() &&
                  claim?.File_Name?.Pdf_File?.toString().trim() && (
                    <button
                      onClick={() =>
                        handleDownloadClaimEop(
                          billingPro,
                          claimNo,
                          claim?.File_Name?.Pdf_File,
                          chqno,
                        )
                      }
                      style={{
                        padding: "10px 20px",
                        borderRadius: 12,
                        border: "none",
                        background: "#fff",
                        color: "#0F766E",
                        fontWeight: 600,
                        fontSize: 13,
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                    >
                      {claimEopDownload
                        ? "Downloading Claim EOP..."
                        : "Download Claim EOP"}
                      {/*Download Claim EOP*/}
                    </button>
                  )}

                {/* Download the 835 ERA (EDI) for this claim's check — the full remittance, all claims on it */}
                {/*{claim?.Payee?.[0]?.Check_Number?.toString().trim() && (*/}
                {/*    <button*/}
                {/*        onClick={async () => {*/}
                {/*            try {*/}
                {/*                await eopApi.download835({*/}
                {/*                    checkNumber: claim?.Payee?.[0]?.Check_Number || chqno,*/}
                {/*                    claimNumber: claim?.Details?.[0]?.Claim_Number || claimNo,*/}
                {/*                    billingProvider: billingPro,*/}
                {/*                });*/}
                {/*            } catch (e) {*/}
                {/*                toast.error(e?.response?.data?.Message || e?.message || 'Could not download the 835.', { title: 'ERA download' });*/}
                {/*            }*/}
                {/*        }}*/}
                {/*        title="Download the X12 835 remittance (ERA) for this check — import into your billing system"*/}
                {/*        style={{*/}
                {/*            padding: '10px 20px', borderRadius: 12, border: 'none',*/}
                {/*            background: '#fff', color: '#0F766E', fontWeight: 600, fontSize: 13,*/}
                {/*            cursor: 'pointer', fontFamily: 'inherit',*/}
                {/*        }}*/}
                {/*    >Download 835 (ERA)</button>*/}
                {/*)}*/}

                {claim?.Payee?.[0]?.Check_Number?.toString().trim() &&
                  (user?.userRole?.trim().toLowerCase() === "super admin" ||
                    user?.userRole?.trim().toLowerCase() === "admin" ||
                    claim?.ERARetrivalMethod?.trim().toLowerCase() ===
                      "download from portal") && (
                    <button
                      onClick={async () => {
                                          try {
                                              toast.success(
                                                  "This feature is currently under development and will be available soon. Thank you for your patience.",
                                                  { title: "ERA Download" },
                                              );
                          //await eopApi.download835({
                          //  checkNumber:
                          //    claim?.Payee?.[0]?.Check_Number || chqno,
                          //  claimNumber:
                          //    claim?.Details?.[0]?.Claim_Number || claimNo,
                          //  billingProvider: billingPro,
                          //});
                        } catch (e) {
                          toast.error(
                            e?.response?.data?.Message ||
                              e?.message ||
                              "Could not download the 835.",
                            { title: "ERA download" },
                          );
                        }
                      }}
                      title="Download the X12 835 remittance (ERA) for this check — import into your billing system"
                      style={{
                        padding: "10px 20px",
                        borderRadius: 12,
                        border: "none",
                        background: "#fff",
                        color: "#0F766E",
                        fontWeight: 600,
                        fontSize: 13,
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                    >
                      Download 835 (ERA)
                    </button>
                  )}

                {/* File an appeal / reconsideration for this claim (prefills the appeal form) */}
                {(claim?.Status?.trim().toLowerCase() === "processed" ||
                  claim?.Status?.trim().toLowerCase() === "denied" ||
                  claim?.Status?.trim().toLowerCase() === "partial denied" ||
                  claim?.Status?.trim().toLowerCase() === "denied partial") &&
                  !["super admin", "admin"].includes(
                    user?.userRole?.trim().toLowerCase(),
                  ) && (
                    /*allow super admin and admin user to claim file if they comes throigh claim appeal list page*/
                    <>
                      <button
                        onClick={() =>
                          navigate(
                            `/claims/${encodeURIComponent(claimNo)}/appeal`,
                            {
                              state: {
                                claimNumber:
                                  claim?.Details?.[0]?.Claim_Number || claimNo,
                                claimStatus:
                                  claim?.Details?.[0]?.Claim_Status ||
                                  claim?.status ||
                                  "",
                                memberName:
                                  claim?.Details?.[0]?.Member_Name || "",
                                memberId: claim?.Details?.[0]?.Member_Id || "",
                                providerName: claim?.Payee?.[0]?.Name || "",
                                federalId: claim?.Payee?.[0]?.Federal_ID || "",
                                selectedUserId: savedState?.selectedUserId,
                              },
                            },
                          )
                        }
                        title="File an appeal / request reconsideration for this claim"
                        style={{
                          padding: "10px 20px",
                          borderRadius: 12,
                          border: "none",
                          background: "#fff",
                          color: "#0F766E",
                          fontWeight: 700,
                          fontSize: 13,
                          cursor: "pointer",
                          fontFamily: "inherit",
                        }}
                      >
                        File Appeal
                      </button>
                    </>
                  )}
                {(claim?.Status?.trim().toLowerCase() === "processed" ||
                  claim?.Status?.trim().toLowerCase() === "denied" ||
                  claim?.Status?.trim().toLowerCase() === "partial denied" ||
                  claim?.Status?.trim().toLowerCase() === "denied partial") &&
                  ["super admin", "admin"].includes(
                    user?.userRole?.trim().toLowerCase(),
                  ) &&
                  savedState?.selectedUserId && (
                    /*allow super admin and admin user to claim file if they comes throigh claim appeal list page*/
                    <>
                      <button
                        onClick={() =>
                          navigate(
                            `/claims/${encodeURIComponent(claimNo)}/appeal`,
                            {
                              state: {
                                claimNumber:
                                  claim?.Details?.[0]?.Claim_Number || claimNo,
                                claimStatus:
                                  claim?.Details?.[0]?.Claim_Status ||
                                  claim?.status ||
                                  "",
                                memberName:
                                  claim?.Details?.[0]?.Member_Name || "",
                                memberId: claim?.Details?.[0]?.Member_Id || "",
                                providerName: claim?.Payee?.[0]?.Name || "",
                                federalId: claim?.Payee?.[0]?.Federal_ID || "",
                                selectedUserId: savedState?.selectedUserId,
                              },
                            },
                          )
                        }
                        title="File an appeal / request reconsideration for this claim"
                        style={{
                          padding: "10px 20px",
                          borderRadius: 12,
                          border: "none",
                          background: "#fff",
                          color: "#0F766E",
                          fontWeight: 700,
                          fontSize: 13,
                          cursor: "pointer",
                          fontFamily: "inherit",
                        }}
                      >
                        File Appeal
                      </button>
                    </>
                  )}
              </div>
            </div>
            <div className="col-sm-2 m-2">
              <div
                style={{ display: "flex", gap: 10, justifyContent: "center" }}
              >
                <button
                  /*onClick={() => navigate('/claims')}*/
                  onClick={
                    savedState.fromClaimAppealList
                      ? goBackToAppeal
                      : goBackToClaims
                  }
                  // onClick={handleGoBack}

                  style={{
                    padding: "10px 20px",
                    borderRadius: 12,
                    border: "1px solid rgba(255,255,255,0.3)",
                    background: "rgba(255,255,255,0.1)",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  &#8592; Go Back
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* TabStrip */}
        <TabStrip
          selected={selectedTab}
          onSelect={(e) => setSelectedTab(e.selected)}
          tabPosition="top"
        >
          {/* Tab 1: Claim Summary
            Driven by the new API shape (`claim.Payee`, `claim.Payor`, `claim.Details`).
            Falls back to a single synthetic Detail row built from old mockData
            fields so existing demo claims still render. */}
          <TabStripTab title="Claim Summary">
            {(() => {
              // ── Normalize whichever shape we got ──────────────────────
              const payee = (claim.Payee && claim.Payee[0]) || {
                Name: claim.providerName,
                Federal_ID: claim.billingProviderId,
                Check_Number: claim.checkNumber,
                Check_Date: claim.checkDate,
              };
              const payor = (claim.Payor && claim.Payor[0]) || {
                Name: "Sendero Health Plans",
                Address: "1111 E. Cesar Chavez",
                City: "Austin",
                State: "TX",
                Zip_Code: "78702",
                Phone_Number: "8448004693",
              };

              // Build the Details array. Priority order:
              //   1. API-supplied claim.Details (each item → one procedure card)
              //   2. mockData claim.lineItems  (each line item → one procedure
              //      card, so multi-procedure claims like CLM-2026-0156 show
              //      separate cards for procedure 97110 and 97140)
              //   3. Single synthesized row from top-level claim fields
              const details = (() => {
                if (Array.isArray(claim.Details) && claim.Details.length > 0) {
                  return claim.Details;
                }
                if (
                  Array.isArray(claim.lineItems) &&
                  claim.lineItems.length > 0
                ) {
                  return claim.lineItems.map((li, i) => ({
                    Claim_Number: claim.claimNumber,
                    Claim_Seq: String(li.lineNumber || i + 1).padStart(2, "0"),
                    Account_No: claim.patientAccountNo,
                    Member_Id: claim.memberId,
                    Member_Name: claim.memberName,
                    Procedure_Code: li.procedureCode,
                    Modifier: li.modifier ?? null,
                    Service_Date: li.serviceDate || claim.serviceDate,
                    Service_Thru: li.serviceDate || claim.serviceDate,
                    Svc_Count: li.units || 1,
                    Billed_Price: fmtCurrency(li.billedPrice),
                    Plan_Price: fmtCurrency(li.planPrice),
                    Discount_Amt: fmtCurrency(li.discountAmount),
                    Paid_Amt: fmtCurrency(li.paidAmount),
                    Copay_Amt: "$0.00",
                    Not_Covered_Amt: "$0.00",
                    Deductible_Amt: "$0.00",
                    Co_Ins_Amt: "$0.00",
                    COB_Amt: "$0.00",
                    Ex_Code: li.exCode || null,
                    Ex_Description: li.exDescription || null,
                    Your_Rep: fmtCurrency(li.memberResponsibility),
                    Services_Desc: li.serviceDescription,
                    Services_Provided: "Professional",
                  }));
                }
                // Final fallback: one synthesized row
                return [
                  {
                    Claim_Number: claim.claimNumber,
                    Claim_Seq: "01",
                    Account_No: claim.patientAccountNo,
                    Member_Id: claim.memberId,
                    Member_Name: claim.memberName,
                    Procedure_Code: claim.procedureCode,
                    Modifier: null,
                    Service_Date: claim.serviceDate,
                    Service_Thru: claim.serviceDate,
                    Svc_Count: 1,
                    Billed_Price: fmtCurrency(claim.billedAmount),
                    Plan_Price: fmtCurrency(planPrice),
                    Discount_Amt: fmtCurrency(discountAmount),
                    Paid_Amt: fmtCurrency(paidAmount),
                    Copay_Amt: "$0.00",
                    Not_Covered_Amt: "$0.00",
                    Deductible_Amt: "$0.00",
                    Co_Ins_Amt: "$0.00",
                    COB_Amt: "$0.00",
                    Ex_Code: claim.exCode || null,
                    Ex_Description: null,
                    Your_Rep: fmtCurrency(memberResponsibility),
                    Services_Desc:
                      claim.serviceDescription || claim.statusDescription,
                    Services_Provided: "Professional",
                  },
                ];
              })();

              const head = details[0] || {};
              const Received_Date = claim.Received_Date[0].Received_Date;

              // Derive a friendly status label from the claim's overall status.
              // "Paid" → "Claim Paid in Full" (matches the design template).
              const statusLabel = (() => {
                const s = (claim.status || "").toLowerCase();
                if (s === "paid") return "Claim Paid in Full";
                if (s === "pending") return "Claim Pending";
                if (s === "denied") return "Claim Denied";
                return claim.status ? `Claim ${claim.status}` : "Claim";
              })();

              // Per-procedure rendering — Single Unified Stat Strip layout.
              // One compact card per procedure with:
              //   Row 1: Big stat numbers (Billed · Allowed · Paid · Adjustments · Units)
              //   Row 2: Progress bar showing paid ratio + status label
              //   Row 3: Inline service details (Description · Service · Modifier · Ex Code)
              //   Row 4: Optional adjustments breakdown chip (only when total > 0)
              const renderProcedureCard = (d, idx) => {
                const billedNum = parseAmount(d.Billed_Price);
                const paidNum = parseAmount(d.Paid_Amt);
                const paidRatio =
                  billedNum > 0
                    ? Math.min(100, Math.round((paidNum / billedNum) * 100))
                    : 0;
                const adjustmentsTotal =
                  parseAmount(d.Discount_Amt) +
                  parseAmount(d.Copay_Amt) +
                  parseAmount(d.Deductible_Amt) +
                  parseAmount(d.Co_Ins_Amt) +
                  parseAmount(d.Not_Covered_Amt) +
                  parseAmount(d.COB_Amt);
                const isPaidFull = paidNum >= billedNum && billedNum > 0;
                const statusBadge = isPaidFull
                  ? "Paid in Full"
                  : paidNum > 0
                    ? "Partially Paid"
                    : "Pending";
                const statusColor = isPaidFull
                  ? "#059669"
                  : paidNum > 0
                    ? "#F59E0B"
                    : "#6B7280";

                // Re-usable stat block: small uppercase label + big bold value.
                const Stat = ({
                  label,
                  value,
                  color,
                  valuePrefix = "",
                  valueSuffix = "",
                }) => (
                  <div style={{ minWidth: 110, flex: "1 1 110px" }}>
                    <div
                      style={{
                        fontSize: 11,
                        color: "#9CA3AF",
                        textTransform: "uppercase",
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                        marginBottom: 6,
                      }}
                    >
                      {label}
                    </div>
                    <div
                      style={{
                        fontSize: 24,
                        fontWeight: 800,
                        color: color || "#1A1D21",
                        lineHeight: 1,
                        letterSpacing: "-0.3px",
                        fontVariantNumeric: "tabular-nums",
                        display: "flex",
                        alignItems: "baseline",
                        gap: 4,
                        flexWrap: "wrap",
                      }}
                    >
                      {valuePrefix && (
                        <span
                          style={{
                            fontSize: 14,
                            color: "#9CA3AF",
                            fontWeight: 600,
                          }}
                        >
                          {valuePrefix}
                        </span>
                      )}
                      {value}
                      {valueSuffix && (
                        <span
                          style={{
                            fontSize: 12,
                            color: "#9CA3AF",
                            fontWeight: 600,
                          }}
                        >
                          {valueSuffix}
                        </span>
                      )}
                    </div>
                  </div>
                );

                // Inline service-detail item: bold label + plain value.
                const Detail = ({ label, value }) => (
                  <div>
                    <strong style={{ color: "#1A1D21" }}>{label}:</strong>
                    <span style={{ color: "#475569", marginLeft: 4 }}>
                      {fmtNullDash(value)}
                    </span>
                  </div>
                );

                return (
                  <div key={`proc-${idx}`} style={{ marginBottom: 16 }}>
                    {/* Header — no card frame, just text */}
                    <div style={{ padding: "4px 4px 14px" }}>
                      <div style={{ fontSize: 16, color: "#1A1D21" }}>
                        <strong>{statusLabel}</strong>
                        {d.Services_Provided && (
                          <span style={{ color: "#6B7280", fontWeight: 500 }}>
                            {" "}
                            · {d.Services_Provided}
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          marginTop: 6,
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: 6,
                          fontSize: 12,
                          fontWeight: 700,
                          color: "#0F766E",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                        }}
                      >
                        <span>Seq {d.Claim_Seq}</span>
                        <span style={{ color: "#94A3B8" }}>·</span>
                        <span>Procedure {d.Procedure_Code}</span>
                        <span style={{ color: "#94A3B8" }}>·</span>
                        <span>Service {formatDate(d.Service_Date)}</span>
                      </div>
                    </div>

                    {/* Single unified card */}
                    <div
                      className="card"
                      style={{
                        marginBottom: 0,
                        padding: "20px 24px",
                        background: `linear-gradient(135deg, ${statusColor}05, #ffffff 70%)`,
                        borderColor: `${statusColor}22`,
                      }}
                    >
                      {/* Row 1 — Stat strip */}
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "20px 32px",
                          alignItems: "flex-start",
                        }}
                      >
                        <Stat
                          label="Billed"
                          value={fmtCurrency(fmtNullDash(d.Billed_Price))}
                        />
                        <Stat
                          label="Allowed"
                          value={fmtCurrency(fmtNullDash(d.Plan_Price))}
                        />
                        <Stat
                          label="Paid"
                          value={fmtCurrency(fmtNullDash(d.Paid_Amt))}
                          color={statusColor}
                        />
                        <Stat
                          label="Adjustments"
                          value={fmtCurrency(adjustmentsTotal)}
                          color={adjustmentsTotal > 0 ? "#D97706" : "#1A1D21"}
                        />
                        <Stat
                          label="Units"
                          value={d.Svc_Count ?? 1}
                          valueSuffix="svc"
                        />
                      </div>

                      {/* Row 2 — Progress bar + status text */}
                      {/*<div style={{ marginTop: 18 }}>*/}
                      {/*    <div style={{ height: 10, borderRadius: 5, background: '#E8ECF0', overflow: 'hidden' }}>*/}
                      {/*        <div style={{*/}
                      {/*            width: `${paidRatio}%`,*/}
                      {/*            height: '100%',*/}
                      {/*            background: `linear-gradient(90deg, ${statusColor}, ${statusColor}DD)`,*/}
                      {/*            transition: 'width 0.3s',*/}
                      {/*        }} />*/}
                      {/*    </div>*/}
                      {/*    <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: statusColor, fontWeight: 700 }}>*/}
                      {/*        <span>{statusBadge}</span>*/}
                      {/*        <span>{paidRatio}% paid</span>*/}
                      {/*    </div>*/}
                      {/*</div>*/}

                      {/* Row 3 — Divider */}
                      <div
                        style={{
                          height: 1,
                          background: "#E8ECF0",
                          margin: "18px 0 14px",
                        }}
                      />

                      {/* Row 4 — Inline service details (Description, Service
                        Thru, Modifier, Ex Code). Ex Description is rendered
                        as its own row below with Show more / Show less. */}
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "8px 24px",
                          fontSize: 13,
                          color: "#475569",
                        }}
                      >
                        <Detail label="Description" value={d.Services_Desc} />
                        <Detail
                          label="Service Thru"
                          value={formatDate(d.Service_Thru)}
                        />
                        <Detail label="Modifier" value={d.Modifier} />
                        <Detail label="Ex Code" value={d.Ex_Code} />
                      </div>

                      {/* Row 4b — Ex Description with show-more toggle */}
                      <ExDescriptionInline text={d.Ex_Description} />

                      {/* Row 5 — Adjustments breakdown (only when total > 0).
                        Uses CSS grid with auto-fit so items snap into
                        2/3/6 columns based on width and never produce
                        orphan dot separators on line wrap. */}
                      {adjustmentsTotal > 0 && (
                        <div
                          style={{
                            marginTop: 12,
                            padding: "10px 14px",
                            background: "rgba(245,158,11,0.06)",
                            border: "1px solid rgba(245,158,11,0.20)",
                            borderRadius: 8,
                            fontSize: 12,
                            color: "#92400E",
                          }}
                        >
                          <strong
                            style={{
                              color: "#92400E",
                              display: "block",
                              marginBottom: 6,
                            }}
                          >
                            Adjustments breakdown:
                          </strong>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns:
                                "repeat(auto-fit, minmax(110px, 1fr))",
                              gap: "4px 16px",
                            }}
                          >
                            <span>Discount {fmtNullDash(d.Discount_Amt)}</span>
                            <span>Copay {fmtNullDash(d.Copay_Amt)}</span>
                            <span>
                              Deductible {fmtNullDash(d.Deductible_Amt)}
                            </span>
                            <span>Coinsurance {fmtNullDash(d.Co_Ins_Amt)}</span>
                            <span>
                              Not Covered {fmtNullDash(d.Not_Covered_Amt)}
                            </span>
                            <span>COB {fmtNullDash(d.COB_Amt)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              };

              // The shared `.detail-grid` style is hard-coded at `1fr 1fr`,
              // which leaves a big gap on the right when there are many fields
              // and the screen is wide. Override inline with auto-fit so 3–4
              // columns fill the row on desktop, 2 on tablet, 1 on mobile.
              const wideDetailGrid = {
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "14px 28px",
              };

              return (
                <>
                  {/* Section 1 — Patient & Claim Info (shared from Details[0]) */}
                  <div
                    className="card"
                    style={{ marginTop: 0, marginBottom: 12 }}
                  >
                    <h3
                      style={{
                        margin: "0 0 14px 0",
                        fontSize: 15,
                        fontWeight: 700,
                        color: "#1A1D21",
                      }}
                    >
                      Patient &amp; Claim Info
                    </h3>
                    <div style={wideDetailGrid}>
                      <div className="detail-row">
                        <span className="detail-label">Claim Number</span>
                        <span className="detail-value">
                          {fmtNullDash(head.Claim_Number)}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Account No</span>
                        <span className="detail-value">
                          {fmtNullDash(head.Account_No)}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Member Name</span>
                        <span className="detail-value">
                          {fmtNullDash(head.Member_Name)}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Member Id</span>
                        <span className="detail-value">
                          {fmtNullDash(head.Member_Id)}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Received At</span>
                        <span className="detail-value">
                          {fmtNullDash(formatDate(Received_Date))}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Section 2 — Procedure cards (one card per Details[] row) */}
                  {details.map((d, i) => renderProcedureCard(d, i))}

                  {/* Section 3 — Payee (Provider) */}
                  <div className="card" style={{ marginBottom: 12 }}>
                    <h3
                      style={{
                        margin: "0 0 14px 0",
                        fontSize: 15,
                        fontWeight: 700,
                        color: "#1A1D21",
                      }}
                    >
                      Payee (Provider)
                    </h3>
                    <div style={wideDetailGrid}>
                      <div className="detail-row">
                        <span className="detail-label">Name</span>
                        <span className="detail-value">
                          {fmtNullDash(payee.Name)}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Address</span>
                        <span className="detail-value">
                          {fmtNullDash(payee.Address)}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Address2</span>
                        <span className="detail-value">
                          {fmtNullDash(payee.Address2)}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">City</span>
                        <span className="detail-value">
                          {fmtNullDash(payee.City)}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">State</span>
                        <span className="detail-value">
                          {fmtNullDash(payee.State)}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Zip Code</span>
                        <span className="detail-value">
                          {fmtNullDash(payee.Zip_Code)}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Federal ID</span>
                        <span className="detail-value">
                          {fmtNullDash(payee.Federal_ID)}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Check Date</span>
                        <span className="detail-value">
                          {fmtNullDash(formatDate(payee.Check_Date))}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Check Number</span>
                        <span className="detail-value">
                          {fmtNullDash(payee.Check_Number)}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Check Batch</span>
                        <span className="detail-value">
                          {fmtNullDash(payee.Check_Batch)}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Processed Date</span>
                        <span className="detail-value">
                          {fmtNullDash(formatDate(payee.Processed_Date))}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Section 4 — Payor (Insurance) */}
                  <div className="card" style={{ marginBottom: 0 }}>
                    <h3
                      style={{
                        margin: "0 0 14px 0",
                        fontSize: 15,
                        fontWeight: 700,
                        color: "#1A1D21",
                      }}
                    >
                      Payor (Insurance)
                    </h3>
                    <div style={wideDetailGrid}>
                      <div className="detail-row">
                        <span className="detail-label">Name</span>
                        <span className="detail-value">
                          {fmtNullDash(payor.Name)}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Address</span>
                        <span className="detail-value">
                          {fmtNullDash(payor.Address)}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Address2</span>
                        <span className="detail-value">
                          {fmtNullDash(payor.Address2)}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">City</span>
                        <span className="detail-value">
                          {fmtNullDash(payor.City)}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">State</span>
                        <span className="detail-value">
                          {fmtNullDash(payor.State)}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Zip Code</span>
                        <span className="detail-value">
                          {fmtNullDash(payor.Zip_Code)}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Phone Number</span>
                        <span className="detail-value">
                          {fmtNullDash(payor.Phone_Number)}
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              );
            })()}
          </TabStripTab>

          {/* Tab 2: Line Items
            Reverted to Kendo (Telerik) Grid for sortable headers, paging,
            and column resizing. Columns match the attached spec exactly.
            Per-column footerCell sums the numeric columns (Claim Total). */}
          <TabStripTab title="Line Items">
            {(() => {
              // Use new API shape if present, else synthesize from old mockData.
              const rows =
                Array.isArray(claim.Details) && claim.Details.length > 0
                  ? claim.Details
                  : claim.Details;
              //(lineItems || []).map((li) => ({
              //    Claim_Number: claim.claimNumber,
              //    Claim_Seq: String(li.lineNumber).padStart(2, '0'),
              //    Account_No: claim.patientAccountNo,
              //    Procedure_Code: li.procedureCode,
              //    Modifier: li.modifier,
              //    Service_Date: li.serviceDate,
              //    Service_Thru: li.serviceDate,
              //    Svc_Count: li.units || 1,
              //    Billed_Price: fmtCurrency(li.billedPrice),
              //    Plan_Price: fmtCurrency(li.planPrice),
              //    //Copay_Amt: '$0.00',
              //    Copay_Amt: li.Copay_Amt,

              //    //Co_Ins_Amt: '$0.00',
              //    Co_Ins_Amt: li.Co_Ins_Amt,
              //    Discount_Amt: fmtCurrency(li.discountAmount),
              //    //Not_Covered_Amt: '$0.00',
              //    Not_Covered_Amt: li.Not_Covered_Amt,
              //    //COB_Amt: '$0.00',
              //    COB_Amt: li.COB_Amt,
              //    Paid_Amt: fmtCurrency(li.paidAmount),
              //    Deductible_Amt: '$0.00',
              //    Late_Payment: null,
              //    Ex_Code: li.exCode,
              //}));

              // setLineItemsGridData(rows);

              const dataResultLineItems = process(rows, dataStateLineItems);

              if (rows.length === 0) {
                return (
                  <div
                    className="card"
                    style={{ textAlign: "center", padding: 48 }}
                  >
                    <div style={{ fontSize: 40, marginBottom: 12 }}>
                      &#x1F4C4;
                    </div>
                    <h3 style={{ margin: "0 0 8px 0", color: "#1A1D21" }}>
                      No Line Items
                    </h3>
                    <p style={{ color: "#6B7280", margin: 0 }}>
                      No line item details are available for this claim.
                    </p>
                  </div>
                );
              }

              // ── Pre-compute per-column totals (used by Kendo footerCell) ─
              const totals = {
                Billed_Price: sumAmounts(rows, "Billed_Price"),
                Plan_Price: sumAmounts(rows, "Plan_Price"),
                Copay_Amt: sumAmounts(rows, "Copay_Amt"),
                Co_Ins_Amt: sumAmounts(rows, "Co_Ins_Amt"),
                Discount_Amt: sumAmounts(rows, "Discount_Amt"),
                Not_Covered_Amt: sumAmounts(rows, "Not_Covered_Amt"),
                COB_Amt: sumAmounts(rows, "COB_Amt"),
                Paid_Amt: sumAmounts(rows, "Paid_Amt"),
                Deductible_Amt: sumAmounts(rows, "Deductible_Amt"),
              };

              // Member responsibility total — uses `Your_Rep` from the API
              // when available, otherwise sums the member's portion fields
              // (Copay + Deductible + Coinsurance + Not Covered).
              const yourRepSum = sumAmounts(rows, "Your_Rep");
              const memberRespTotal =
                yourRepSum > 0
                  ? yourRepSum
                  : totals.Copay_Amt +
                    totals.Deductible_Amt +
                    totals.Co_Ins_Amt +
                    totals.Not_Covered_Amt;

              // ── Custom cell renderers ──────────────────────────────────
              // Date Of Service combines Service_Date and Service_Thru
              const DateRangeCell = (props) => {
                //  console.log("PROPS : "+JSON.stringify(props));
                const d = props.dataItem || {};
                const a = d.Service_Date.replaceAll("-", "/");
                const b = d.Service_Thru.replaceAll("-", "/");

                //const newDate = date.replaceAll("-", "/");
                const text = a && b && a !== b ? `${a} - ${b}` : a || b || "—";
                return (
                  <td
                    {...props.tdProps}
                    colSpan={props.colSpan}
                    style={props.style}
                  >
                    {text}
                  </td>
                );
              };

              // Display amount with $ stripped (matches attached image format)
              const AmountCell = (field) => (props) => {
                const v = props.dataItem?.[field];
                return (
                  <td
                    {...props.tdProps}
                    colSpan={props.colSpan}
                    style={{
                      ...props.style,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {fmtAmountNoSign(v)}
                  </td>
                );
              };

              // null → "—" cell
              const NullDashCell = (field) => (props) => {
                const v = props.dataItem?.[field];
                return (
                  <td
                    {...props.tdProps}
                    colSpan={props.colSpan}
                    style={props.style}
                  >
                    {fmtNullDash(v)}
                  </td>
                );
              };

              // Footer cell: shows the precomputed sum with $ prefix in teal.
              // The first column gets a "Claim Total:" label.
              const FooterTotal = (field) => () => (
                <td
                  style={{
                    fontWeight: 700,
                    color: "#0F766E",
                    background: "rgba(13,148,136,0.04)",
                    borderTop: "2px solid #0D9488",
                    fontVariantNumeric: "tabular-nums",
                    padding: "12px 14px",
                  }}
                >
                  {fmtCurrency(totals[field])}
                </td>
              );

              const FooterLabel =
                ({ text = "" } = {}) =>
                () => (
                  <td
                    style={{
                      fontWeight: 800,
                      color: "#0F766E",
                      background: "rgba(13,148,136,0.04)",
                      borderTop: "2px solid #0D9488",
                      textAlign: "right",
                      padding: "12px 14px",
                    }}
                  >
                    {text}
                  </td>
                );

              const FooterEmpty = () => () => (
                <td
                  style={{
                    background: "rgba(13,148,136,0.04)",
                    borderTop: "2px solid #0D9488",
                    padding: "12px 14px",
                  }}
                />
              );

              return (
                <>
                  <div
                    className="card"
                    style={{ padding: 0, overflow: "hidden" }}
                  >
                    <div className="grid-scroll-wrapper">
                      <Grid
                        /*data={rows.slice(lineItemsPage.skip, lineItemsPage.skip + lineItemsPage.take)}*/
                        sortable={true}
                        resizable={true}
                        {...dataStateLineItems}
                        data={dataResultLineItems}
                        sortable={true}
                        pageable={true}
                        onDataStateChange={(e) =>
                          setDataStateLineItems(e.dataState)
                        }
                        pageable={{ pageSizes: [5, 10, 20] }}
                        /*skip={lineItemsPage.skip}*/
                        /*take={lineItemsPage.take}*/
                        /*total={rows.length}*/
                        /*onPageChange={(e) => setLineItemsPage({ skip: e.page.skip, take: e.page.take })}*/
                        style={{ border: "none" }}
                      >
                        <GridColumn
                          title="Date Of Service"
                          width="170px"
                          cells={{ data: DateRangeCell }}
                          footerCell={FooterEmpty()}
                        />
                        <GridColumn
                          field="Claim_Number"
                          title="Claim #"
                          width="110px"
                          footerCell={FooterEmpty()}
                        />
                        <GridColumn
                          field="Account_No"
                          title="Acc #"
                          width="100px"
                          footerCell={FooterEmpty()}
                        />
                        <GridColumn
                          field="Procedure_Code"
                          title="Procedure"
                          width="110px"
                          footerCell={FooterEmpty()}
                        />
                        <GridColumn
                          field="Modifier"
                          title="Mod."
                          width="80px"
                          cells={{ data: NullDashCell("Modifier") }}
                          footerCell={FooterLabel({ text: "Claim Total:" })()}
                        />
                        <GridColumn
                          field="Svc_Count"
                          title="Units"
                          width="80px"
                          footerCell={FooterEmpty()}
                        />
                        <GridColumn
                          field="Billed_Price"
                          title="Billed Price"
                          width="120px"
                          cells={{ data: AmountCell("Billed_Price") }}
                          footerCell={FooterTotal("Billed_Price")()}
                        />
                        <GridColumn
                          field="Plan_Price"
                          title="Allowed"
                          width="110px"
                          cells={{ data: AmountCell("Plan_Price") }}
                          footerCell={FooterTotal("Plan_Price")()}
                        />
                        <GridColumn
                          field="Copay_Amt"
                          title="Co-Pay"
                          width="100px"
                          cells={{ data: AmountCell("Copay_Amt") }}
                          footerCell={FooterTotal("Copay_Amt")()}
                        />
                        <GridColumn
                          field="Co_Ins_Amt"
                          title="Co-Ins"
                          width="100px"
                          cells={{ data: AmountCell("Co_Ins_Amt") }}
                          footerCell={FooterTotal("Co_Ins_Amt")()}
                        />
                        <GridColumn
                          field="Discount_Amt"
                          title="Ded"
                          width="100px"
                          cells={{ data: AmountCell("Discount_Amt") }}
                          footerCell={FooterTotal("Discount_Amt")()}
                        />
                        <GridColumn
                          field="Not_Covered_Amt"
                          title="Not Covered"
                          width="120px"
                          cells={{ data: AmountCell("Not_Covered_Amt") }}
                          footerCell={FooterTotal("Not_Covered_Amt")()}
                        />
                        <GridColumn
                          field="COB_Amt"
                          title="COB"
                          width="100px"
                          cells={{ data: AmountCell("COB_Amt") }}
                          footerCell={FooterTotal("COB_Amt")()}
                        />
                        <GridColumn
                          field="Paid_Amt"
                          title="Paid"
                          width="100px"
                          cells={{ data: AmountCell("Paid_Amt") }}
                          footerCell={FooterTotal("Paid_Amt")()}
                        />
                        <GridColumn
                          field="Deductible_Amt"
                          title="Deductible Amt"
                          width="140px"
                          cells={{ data: AmountCell("Deductible_Amt") }}
                          footerCell={FooterTotal("Deductible_Amt")()}
                        />
                        <GridColumn
                          field="Late_Payment"
                          title="Late Payment"
                          width="120px"
                          cells={{ data: NullDashCell("Late_Payment") }}
                          footerCell={FooterEmpty()}
                        />
                        <GridColumn
                          field="Ex_Code"
                          title="Ex Codes"
                          width="100px"
                          cells={{ data: NullDashCell("Ex_Code") }}
                          footerCell={FooterEmpty()}
                        />
                      </Grid>
                    </div>
                  </div>

                  {/* ── Line Items Totals summary card ───────────────────────
                  5 prominent stat cards: Billed · Paid (green) · Plan
                  Price · Discount (orange) · Member Resp. */}
                  <div
                    className="card"
                    style={{
                      marginTop: 16,
                      background: "rgba(13, 148, 136, 0.04)",
                      border: "1px solid rgba(13, 148, 136, 0.15)",
                    }}
                  >
                    <h3
                      style={{
                        margin: "0 0 18px 0",
                        fontSize: 16,
                        fontWeight: 700,
                        color: "#1A1D21",
                      }}
                    >
                      Line Items Totals
                    </h3>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "20px 48px",
                      }}
                    >
                      <div style={{ minWidth: 110 }}>
                        <div
                          style={{
                            fontSize: 11,
                            color: "#6B7280",
                            textTransform: "uppercase",
                            fontWeight: 700,
                            letterSpacing: "0.08em",
                            marginBottom: 8,
                          }}
                        >
                          Billed
                        </div>
                        <div
                          style={{
                            fontSize: 24,
                            fontWeight: 800,
                            color: "#1A1D21",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {fmtCurrency(totals.Billed_Price)}
                        </div>
                      </div>
                      <div style={{ minWidth: 110 }}>
                        <div
                          style={{
                            fontSize: 11,
                            color: "#6B7280",
                            textTransform: "uppercase",
                            fontWeight: 700,
                            letterSpacing: "0.08em",
                            marginBottom: 8,
                          }}
                        >
                          Paid
                        </div>
                        <div
                          style={{
                            fontSize: 24,
                            fontWeight: 800,
                            color: "#059669",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {fmtCurrency(totals.Paid_Amt)}
                        </div>
                      </div>
                      <div style={{ minWidth: 110 }}>
                        <div
                          style={{
                            fontSize: 11,
                            color: "#6B7280",
                            textTransform: "uppercase",
                            fontWeight: 700,
                            letterSpacing: "0.08em",
                            marginBottom: 8,
                          }}
                        >
                          Plan Price
                        </div>
                        <div
                          style={{
                            fontSize: 24,
                            fontWeight: 800,
                            color: "#1A1D21",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {fmtCurrency(totals.Plan_Price)}
                        </div>
                      </div>
                      <div style={{ minWidth: 110 }}>
                        <div
                          style={{
                            fontSize: 11,
                            color: "#6B7280",
                            textTransform: "uppercase",
                            fontWeight: 700,
                            letterSpacing: "0.08em",
                            marginBottom: 8,
                          }}
                        >
                          Discount
                        </div>
                        <div
                          style={{
                            fontSize: 24,
                            fontWeight: 800,
                            color: "#D97706",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {fmtCurrency(totals.Discount_Amt)}
                        </div>
                      </div>
                      <div style={{ minWidth: 110 }}>
                        <div
                          style={{
                            fontSize: 11,
                            color: "#6B7280",
                            textTransform: "uppercase",
                            fontWeight: 700,
                            letterSpacing: "0.08em",
                            marginBottom: 8,
                          }}
                        >
                          Member Resp.
                        </div>
                        <div
                          style={{
                            fontSize: 24,
                            fontWeight: 800,
                            color: "#1A1D21",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {fmtCurrency(memberRespTotal)}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              );
            })()}
          </TabStripTab>

          {/* Tab 3: EOP History */}
          <TabStripTab title="EOP History">
            <div style={{ marginTop: 0 }}>
              {eopHistory.length === 0 ? (
                <div
                  className="card"
                  style={{ textAlign: "center", padding: 48 }}
                >
                  <div style={{ fontSize: 40, marginBottom: 12 }}>
                    &#x1F50D;
                  </div>
                  <h3 style={{ margin: "0 0 8px 0", color: "#1A1D21" }}>
                    No EOP Records Found
                  </h3>
                  <p style={{ color: "#6B7280", margin: 0 }}>
                    There are no EOP records associated with this claim.
                  </p>
                </div>
              ) : (
                <div className="grid-scroll-wrapper">
                  <Grid
                    data={eopHistory.slice(
                      eopHistoryPage.skip,
                      eopHistoryPage.skip + eopHistoryPage.take,
                    )}
                    sortable={true}
                    pageable={{ pageSizes: [5, 10, 20] }}
                    skip={eopHistoryPage.skip}
                    take={eopHistoryPage.take}
                    total={eopHistory.length}
                    onPageChange={(e) =>
                      setEopHistoryPage({
                        skip: e.page.skip,
                        take: e.page.take,
                      })
                    }
                    style={{ border: "none" }}
                  >
                    {/*  <GridColumn field="Ex_Code" title="Ex Code" width="49px" />*/}
                    {/*  <GridColumn field="Ex_Description" title="Ex Description" width="160px" />*/}
                    {/*   <GridColumn field="checkDate" title="Check Date" width="150px" />*/}
                    {/*<GridColumn field="paymentAmount" title="Payment Amount" width="150px" cells={{ data: EOPAmountCell }} />*/}
                    {/*<GridColumn field="paymentMethod" title="Payment Method" width="140px" /> */}

                    <GridColumn field="eopNumber" title="Batch" />
                    <GridColumn field="checkNumber" title="Check #" />
                    <GridColumn field="checkDate" title="Check Date" />
                    <GridColumn field="paymentAmount" title="Amount" />
                  </Grid>
                </div>
              )}
            </div>
          </TabStripTab>
        </TabStrip>

        {/* TabStrip Teal Active Styling */}
        <style>{`
        .k-tabstrip .k-tabstrip-items-wrapper {
          border-bottom: 2px solid #E8ECF0;
        }
        .k-tabstrip .k-tabstrip-items-wrapper .k-item {
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          font-size: 14px;
          color: #6B7280;
          padding: 12px 20px;
          border: none;
          background: transparent;
          transition: color 0.15s ease;
        }
        .k-tabstrip .k-tabstrip-items-wrapper .k-item:hover {
          color: #0D9488;
          background: rgba(13, 148, 136, 0.04);
        }
        .k-tabstrip .k-tabstrip-items-wrapper .k-item.k-active,
        .k-tabstrip .k-tabstrip-items-wrapper .k-item.k-selected {
          color: #0F766E;
          border: none;
          border-bottom: 3px solid #0D9488;
          background: rgba(13, 148, 136, 0.06);
        }
        .k-tabstrip .k-content,
        .k-tabstrip .k-tabstrip-content {
          border: none;
          padding: 20px 0 0 0;
          background: transparent;
        }

        /* The procedure card uses inline flexbox that auto-wraps the
           5-stat strip on narrow screens — no media-query layout switch
           needed. The flex wrap drops stats to multiple rows naturally. */

        /* Compact Insight Cards (Claim Summary -1) — 3 cards desktop,
           2 tablet, 1 mobile. */
        @media (max-width: 1024px) {
          .procedure-insight-row {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .procedure-insight-row > div:nth-child(3) {
            grid-column: 1 / -1;
          }
        }
        @media (max-width: 640px) {
          .procedure-insight-row {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
      </div>
    </>
  );
};

export default ClaimDetailPage;
